// ---- In-memory TTL cache ---------------------------------------------------
// Shared singleton used by all service modules to avoid redundant DB round-trips.
// The cache is cleared entirely on logout and selectively invalidated after writes.

interface CacheEntry<T> {
  data: T
  expiresAt: number
}

class MemCache {
  private store = new Map<string, CacheEntry<unknown>>()

  get<T>(key: string): T | null {
    const entry = this.store.get(key)
    if (!entry) return null
    if (Date.now() > entry.expiresAt) {
      this.store.delete(key)
      return null
    }
    return entry.data as T
  }

  set<T>(key: string, data: T, ttlMs: number): void {
    this.store.set(key, { data, expiresAt: Date.now() + ttlMs })
  }

  invalidate(key: string): void {
    this.store.delete(key)
  }

  /** Removes all entries whose key starts with the given prefix. */
  invalidatePrefix(prefix: string): void {
    for (const key of this.store.keys()) {
      if (key.startsWith(prefix)) {
        this.store.delete(key)
      }
    }
  }

  /** Clears everything — called on logout. */
  clear(): void {
    this.store.clear()
  }

  /**
   * Returns the cached value when fresh.
   * If stale/missing, runs `fn`, stores the result, and returns it.
   */
  async getOrFetch<T>(key: string, ttlMs: number, fn: () => Promise<T>): Promise<T> {
    const cached = this.get<T>(key)
    if (cached !== null) return cached
    const data = await fn()
    this.set(key, data, ttlMs)
    return data
  }

  /**
   * Like getOrFetch but de-duplicates concurrent in-flight requests.
   * Multiple callers that hit a cold key at the same time all wait for the
   * same single fetch instead of each firing their own.
   */
  private inFlight = new Map<string, Promise<unknown>>()

  async getOrFetchDeduped<T>(key: string, ttlMs: number, fn: () => Promise<T>): Promise<T> {
    const cached = this.get<T>(key)
    if (cached !== null) return cached

    const existing = this.inFlight.get(key)
    if (existing) return existing as Promise<T>

    const promise = fn().then((data) => {
      this.set(key, data, ttlMs)
      this.inFlight.delete(key)
      return data
    }).catch((err) => {
      this.inFlight.delete(key)
      throw err
    })

    this.inFlight.set(key, promise)
    return promise as Promise<T>
  }
}

export const memCache = new MemCache()

// ---- TTL constants (milliseconds) ------------------------------------------
export const TTL = {
  /** Resolved auth-user-id → patient/provider row id.  Safe to hold for several minutes. */
  ACTOR_ID: 5 * 60_000,
}
